// Generate IDs

import {PERIOD} from '../../EditProcess/transactions';
import {isNumeric} from '../../../../../components/SmartInput';

export const generateNewRuleId = rules =>
  rules.length > 0 ? Math.max(...rules.map(rule => rule.id)) + 1 : 1;

export const generateNewGroupId = groups =>
  groups.length > 0 ? Math.max(...groups.map(group => group.id)) + 1 : 1;

export const generateNewConditionId = conditions =>
  conditions.length > 0
    ? Math.max(...conditions.map(condition => condition.id)) + 1
    : 1;
export function transformRulesData(rulesData) {
  return rulesData.map((rule, ruleIndex) => ({
    // or use a specific id mapping if available
    name: rule.name,
    description: rule.description,
    ...rule,
    id: ruleIndex === 0 ? 1 : ruleIndex + 1,
    ruleId: rule.id,
    conditionType: rule.currentRuleCondition === 'all' ? 'and' : 'or',
    groups: rule.configurations.map((group, groupIndex) => {
      const [conditionType] = Object.keys(group); // "any" or "all"
      const conditions = group[conditionType].map(
        (condition, conditionIndex) => ({
          id: conditionIndex + 1,
          parent: '',
          field: condition.fact || '',
          operator: condition.operator || '',
          value: condition.value || '',
        }),
      );

      return {
        id: groupIndex + 1,
        conditionType: conditionType === 'all' ? 'and' : 'or',
        conditions,
      };
    }),
  }));
}
export function transformRulesDataForApi(ruleData) {
  return ruleData.map(rule => {
    const topConditionKey = rule.conditionType === 'and' ? 'all' : 'any';
    const transformedConditions = rule.groups.map(group => {
      const groupKey = group.conditionType === 'and' ? 'all' : 'any';
      const groupConditions = group.conditions.map(condition => ({
        fact: condition.field,
        operator: condition.operator,
        value: isNumeric(condition.value)
          ? Number(condition?.value?.replaceAll(',', ''))
          : condition.value,
      }));

      return {
        [groupKey]: groupConditions,
      };
    });

    const {
      groups,
      configurations,
      conditionType,
      key,
      ruleId,
      risk_score,
      createdAt,
      type,
      currentRuleCondition,
      isRuleOpen,
      ...rest
    } = rule;

    const action = {
      low_risk: 'ALLOW',
      high_risk: 'BLOCK',
      medium_risk: 'REVIEW',
    }[rest?.event?.type];

    return {
      ...rest,
      id: ruleId,
      name: rest.name || 'Rule Name',
      description: rest.description || 'Description',
      conditions: {
        [topConditionKey]: transformedConditions,
      },
      event: {
        ...rest.event,
        params: {
          message: rest.name,
          ...rest?.event?.params,
          action,
          risk_score,
        },
      },
    };
  });
}
// Create new items
export const createNewRule = ruleId => ({
  id: ruleId,
  name: `Rule ${ruleId}`,
  conditionType: 'and',
  isRuleOpen: true,
  groups: [
    {
      id: 1,
      conditionType: 'and',
      conditions: [{id: 1, field: '', operator: '', value: ''}],
    },
  ],
});
export const createNewGroup = groupId => ({
  id: groupId,
  conditionType: 'and',
  conditions: [{id: 1, field: '', operator: '', value: ''}],
});
export const createNewCondition = conditionId => ({
  id: conditionId,
  field: '',
  operator: '',
  value: '',
});

// Rule operations
export const addNewRule = (rules, setRules, position, setCurrentActiveRule) => {
  const newRuleId = generateNewRuleId(rules);
  const newRule = createNewRule(newRuleId);

  setCurrentActiveRule(newRule);

  if (position !== undefined) {
    const newRules = [...rules];
    newRules.splice(position, 0, newRule);
    setRules(newRules);
  } else {
    const oldRules = [...rules];
    setRules([...oldRules, newRule]);
  }
};
export const addNewGroup = (rules, setRules, ruleId) => {
  setRules(
    rules.map(rule => {
      if (rule.id === ruleId) {
        return {
          ...rule,
          groups: [
            ...rule.groups,
            createNewGroup(generateNewGroupId(rule.groups)),
          ],
        };
      }
      return rule;
    }),
  );
};
export const addNewCondition = (rules, setRules, ruleId, groupId) => {
  setRules(
    rules.map(rule => {
      if (rule.id === ruleId) {
        return {
          ...rule,
          groups: rule.groups.map(group => {
            if (group.id === groupId) {
              return {
                ...group,
                conditions: [
                  ...group.conditions,
                  createNewCondition(generateNewConditionId(group.conditions)),
                ],
              };
            }
            return group;
          }),
        };
      }
      return rule;
    }),
  );
};
export const updateConditionType = (rules, setRules, ruleId, type) => {
  setRules(
    rules.map(rule =>
      rule.id === ruleId
        ? {
            ...rule,
            conditionType: type,
          }
        : rule,
    ),
  );
};
export const updateGroupConditionType = (
  rules,
  setRules,
  ruleId,
  groupId,
  type,
) => {
  setRules(
    rules.map(rule =>
      rule.id === ruleId
        ? {
            ...rule,
            groups: rule.groups.map(group =>
              group.id === groupId
                ? {
                    ...group,
                    conditionType: type,
                  }
                : group,
            ),
          }
        : rule,
    ),
  );
};

export const updateCondition = (
  rules,
  ruleId,
  groupId,
  conditionId,
  field,
  value,
  isAggregateFunction,
) =>
  rules.map(rule =>
    rule.id === ruleId
      ? {
          ...rule,
          groups: rule.groups.map(group =>
            group.id === groupId
              ? {
                  ...group,
                  conditions: group.conditions.map(condition =>
                    condition.id === conditionId
                      ? {
                          ...condition,
                          ...(field === 'field' &&
                            !isAggregateFunction && {operator: '', value: ''}),
                          ...(field === 'operator' && {value: ''}),
                          [field]: value,
                        }
                      : condition,
                  ),
                }
              : group,
          ),
        }
      : rule,
  );

export const updateRuleName = (rules, setRules, ruleId, name) => {
  setRules(
    rules.map(rule =>
      rule.id === ruleId
        ? {
            ...rule,
            name,
          }
        : rule,
    ),
  );
};
export const updateRule = ({rules, setRules, ruleId, value, key}) => {
  setRules(
    rules.map(rule =>
      rule.id === ruleId
        ? {
            ...rule,
            ...(['period', 'type', 'name', 'risk_score']?.includes(key) && {
              event: {
                ...rule?.event,
                ...(key === 'type' && {type: value}),
                params: {
                  ...rule?.event?.params,
                  ...(key === 'period' && value),
                  ...(key === 'risk_score' && value),
                  ...(key === 'name' && {message: value}),
                },
              },
            }),

            [key]: value,
          }
        : rule,
    ),
  );
};

export const deleteCondition = (
  rules,
  setRules,
  ruleId,
  groupId,
  conditionId,
) => {
  setRules(
    rules.map(rule =>
      rule.id === ruleId
        ? {
            ...rule,
            groups: rule.groups.map(group =>
              group.id === groupId
                ? {
                    ...group,
                    conditions: group.conditions.filter(
                      condition => condition.id !== conditionId,
                    ),
                  }
                : group,
            ),
          }
        : rule,
    ),
  );
};

export const deleteRule = (rules, setRules, ruleId) => {
  setRules(rules.filter(rule => rule.id !== ruleId));
};

export const deleteGroup = (rules, setRules, ruleId, groupId) => {
  const updatedRules = rules.map(rule => {
    if (rule.id === ruleId) {
      if (rule.groups.length > 1) {
        const updatedGroups = rule.groups.filter(group => group.id !== groupId);
        return {...rule, groups: updatedGroups};
      }
    }
    return rule;
  });

  setRules(updatedRules);
};

export const getAggregateValue = field => {
  const countPeriod = field ? field.slice(field.lastIndexOf('_') + 1) : '';

  const parts = countPeriod?.match(/^(\d+)([a-zA-Z]+)$/);
  const result = parts ? [parts[1], parts[2]] : [];
  const currentCount = result[0];

  const currentPeriod =
    (countPeriod.length > 1 && PERIOD.find(p => p.value === result[1])) || {};

  return {currentCount, currentPeriod};
};

export const upsertByConditionId = (array, newObject) => {
  if (!Array.isArray(array)) return [];
  const index = array?.findIndex(
    item => item.condition === newObject.condition,
  );

  if (index !== -1) {
    array[index] = newObject;
  } else {
    array.push(newObject);
  }

  return array;
};

export function convertToMinutes(value, periodInitial) {
  if (typeof value !== 'number' || isNaN(value)) {
    console.error("Invalid input: 'value' must be a number.");
    return 0;
  }
  const initial = String(periodInitial).toLowerCase();

  switch (initial) {
    case 'm':
      return value;
    case 'h':
      return value * 60;
    case 'd':
      return value * 24 * 60;
    default:
      console.warn(
        `Unrecognized period initial: '${periodInitial}'. Returning original value.`,
      );
      return value;
  }
}
